"use client";

import { useRef, useState } from "react";
import {
  CopyButton,
  ErrorNotice,
  Notice,
  PillGroup,
  SelectField,
  StatCard,
  StatGrid,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  BUTTON_SMALL,
  HINT,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import {
  niceRange,
  PhysicsPlot,
} from "@/components/tools/physics-mechanics/plot";
import { QuantityField } from "@/components/tools/physics-mechanics/quantity-field";
import { Working } from "@/components/tools/physics-mechanics/working";
import {
  useToolLocale,
  useToolNumber,
  useToolUi,
} from "@/components/tools/tool-ui";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { downloadPng } from "@/lib/tool-kit/download";
import { CHART_COLORS } from "@/lib/tool-kit/palette";
import {
  formatPlain,
  formatSig,
  SIG_FIG_CHOICES,
} from "@/lib/tool-kit/physics-mechanics/format";
import {
  KIN_VARS,
  type KinKnown,
  type KinSolution,
  type KinValues,
  type KinVar,
  RULES,
  solveKinematics,
  substituteRule,
} from "@/lib/tool-kit/physics-mechanics/kinematics";
import {
  KIN_DEFAULT_UNITS,
  KIN_FAMILIES,
} from "@/lib/tool-kit/physics-mechanics/kinematics-view";
import { parseQuantity } from "@/lib/tool-kit/physics-mechanics/quantity";
import {
  fromBase,
  isBaseUnit,
  toBase,
  unitOptions,
  unitSymbol,
} from "@/lib/tool-kit/physics-mechanics/units";

/** English strings. A translated content file overrides these through `ui`. */
export const KINEMATICS_CALCULATOR_STRINGS = {
  instructions:
    "Enter any three of the five quantities and leave the other two blank — the blanks are calculated. Use a minus sign for velocity or acceleration in the opposite direction.",
  s: "Displacement s",
  u: "Initial velocity u",
  v: "Final velocity v",
  a: "Acceleration a",
  t: "Time t",
  unitFor: "Unit for {name}",
  calculated: "Calculated",
  rounding: "Round answers to",
  sigFigs: "one: {count} significant figure|other: {count} significant figures",
  clear: "Clear all",
  results: "Results",
  solutionChoice: "Which solution?",
  solutionLabel: "Solution {n}",
  twoSolutions:
    "Two different motions fit these numbers — the object passes the same point twice (for example, on the way up and again on the way down). Switch between them with the buttons.",
  discarded:
    "The equation has a second mathematical solution that needs a negative time, so it was left out.",
  averageVelocity: "Average velocity (s ÷ t)",
  workingHeading: "The working",
  stepSi: "Convert to SI units: {list}.",
  stepKnown: "Known: {known}. Unknown: {unknown}.",
  stepUse: "Find {symbol} from {equation}, rearranged as {symbol} = {rhs}.",
  stepSubstitute: "{symbol} = {working} = {value} {unit}",
  graphHeading: "Velocity–time graph",
  graphTitle:
    "Velocity against time: a straight line from u = {u} to v = {v} over {t}; the shaded area is the displacement",
  xAxis: "Time t ({unit})",
  yAxis: "Velocity ({unit})",
  startPoint: "u = {value}",
  endPoint: "v = {value}",
  graphNote:
    "The slope of the line is the acceleration a. The shaded area between the line and the time axis is the displacement s; area below the axis counts as negative.",
  downloadPng: "Download graph (PNG)",
  copy: "Copy results",
  copied: "Copied!",
  assumptions:
    "Assumes constant acceleration along a straight line. Where the acceleration changes, or the motion is in two dimensions, these equations do not apply.",
  needThree:
    "Enter three of the five values — you have entered {count}. The other two are calculated.",
  tooMany:
    "Leave two boxes blank so there is something to calculate: enter exactly three values, or press Clear all to start again.",
  unreadable:
    "{name} is not a number. Use digits, a decimal point and an optional minus sign, for example −9.8 or 1.5e3.",
  errors: {
    "negative-time-input":
      "Time cannot be negative. Enter a positive time, and use a negative velocity or acceleration for motion in the other direction.",
    "negative-time":
      "Every solution needs a negative time, so the motion described cannot happen going forward from t = 0.",
    "zero-time":
      "With t = 0 there is no motion to divide by, so the equations cannot find acceleration or velocity. Enter a time greater than zero.",
    "no-solution":
      "These three values cannot all be true together for constant acceleration — for example, the object never reaches that displacement, or the velocities and acceleration point the wrong way. Check the signs.",
    undetermined:
      "These three values do not fix the other two uniquely — for example, a = 0 with u = v leaves the time and displacement open. Change one of them.",
    "not-finite":
      "The result is too large to calculate. Check the numbers and units.",
    "need-three": "Enter three values.",
    "too-many": "Enter exactly three values.",
  },
} as const;

type Strings = typeof KINEMATICS_CALCULATOR_STRINGS;
type Fmt = (value: number, options?: Intl.NumberFormatOptions) => string;
interface Entry {
  value: string;
  unit: string;
}
type Entries = Record<KinVar, Entry>;

const INITIAL: Entries = {
  s: { value: "", unit: KIN_DEFAULT_UNITS.s },
  u: { value: "12", unit: KIN_DEFAULT_UNITS.u },
  v: { value: "28", unit: KIN_DEFAULT_UNITS.v },
  a: { value: "", unit: KIN_DEFAULT_UNITS.a },
  t: { value: "8", unit: KIN_DEFAULT_UNITS.t },
};

/** SI unit symbol for a variable. */
function siSymbol(name: KinVar): string {
  return KIN_FAMILIES[name].base;
}

function valueIn(name: KinVar, values: KinValues, unit: string): number {
  return fromBase(values[name], KIN_FAMILIES[name], unit);
}

function workingSteps(
  known: KinKnown,
  entries: Entries,
  solution: KinSolution,
  strings: Strings,
  fmt: Fmt,
  sig: number
): string[] {
  const plain = (value: number) => formatPlain(fmt, value);
  const knownNames = KIN_VARS.filter((name) => known[name] !== undefined);
  const unknownNames = KIN_VARS.filter((name) => known[name] === undefined);
  const steps: string[] = [];
  const converted = knownNames.filter(
    (name) => !isBaseUnit(KIN_FAMILIES[name], entries[name].unit)
  );
  if (converted.length > 0) {
    const list = converted
      .map((name) => {
        const entry = entries[name];
        const typed = parseQuantity(entry.value) ?? 0;
        return `${name} = ${plain(typed)} ${unitSymbol(KIN_FAMILIES[name], entry.unit)} = ${plain(known[name] ?? 0)} ${siSymbol(name)}`;
      })
      .join("; ");
    steps.push(t(strings.stepSi, { list }));
  }
  steps.push(
    t(strings.stepKnown, {
      known: knownNames
        .map((name) => `${name} = ${plain(known[name] ?? 0)} ${siSymbol(name)}`)
        .join(", "),
      unknown: unknownNames.join(", "),
    })
  );
  for (const step of solution.steps) {
    const rule = RULES[step.rule];
    steps.push(
      t(strings.stepUse, {
        symbol: rule.target,
        equation: rule.equation,
        rhs: rule.rhs,
      })
    );
    steps.push(
      t(strings.stepSubstitute, {
        symbol: rule.target,
        working: substituteRule(rule, solution.values, step.sign, plain),
        value: formatSig(fmt, solution.values[rule.target], sig),
        unit: siSymbol(rule.target),
      })
    );
  }
  return steps;
}

function VelocityGraph({
  values,
  entries,
  strings,
  fmt,
  sig,
}: {
  values: KinValues;
  entries: Entries;
  strings: Strings;
  fmt: Fmt;
  sig: number;
}) {
  const ref = useRef<SVGSVGElement>(null);
  const timeUnit = entries.t.unit;
  const speedUnit = entries.v.unit;
  const timeSymbol = unitSymbol(KIN_FAMILIES.t, timeUnit);
  const speedSymbol = unitSymbol(KIN_FAMILIES.v, speedUnit);
  const tEnd = valueIn("t", values, timeUnit);
  const uShown = fromBase(values.u, KIN_FAMILIES.u, speedUnit);
  const vShown = valueIn("v", values, speedUnit);
  const label = (value: number, unit: string) =>
    `${formatSig(fmt, value, sig)} ${unit}`;
  const xRange = niceRange(0, tEnd, 0.05);
  const yRange = niceRange(
    Math.min(0, uShown, vShown),
    Math.max(0, uShown, vShown)
  );
  return (
    <div className="space-y-3">
      <PhysicsPlot
        formatTick={(value, decimals) =>
          fmt(value, { maximumFractionDigits: decimals })
        }
        markers={[
          {
            x: 0,
            y: uShown,
            color: CHART_COLORS[1],
            label: t(strings.startPoint, { value: label(uShown, speedSymbol) }),
            below: uShown > vShown,
          },
          {
            x: tEnd,
            y: vShown,
            color: CHART_COLORS[3],
            label: t(strings.endPoint, { value: label(vShown, speedSymbol) }),
            below: vShown >= uShown,
          },
        ]}
        series={[
          {
            points: [
              { x: 0, y: uShown },
              { x: tEnd, y: vShown },
            ],
            color: CHART_COLORS[0],
            fill: true,
          },
        ]}
        svgRef={ref}
        title={t(strings.graphTitle, {
          u: label(uShown, speedSymbol),
          v: label(vShown, speedSymbol),
          t: label(tEnd, timeSymbol),
        })}
        xLabel={t(strings.xAxis, { unit: timeSymbol })}
        xRange={xRange}
        yLabel={t(strings.yAxis, { unit: speedSymbol })}
        yRange={yRange}
      />
      <p className={HINT}>{strings.graphNote}</p>
      <button
        className={BUTTON_SMALL}
        onClick={() => {
          if (ref.current) {
            downloadPng("velocity-time-graph.png", ref.current).catch(
              () => undefined
            );
          }
        }}
        type="button"
      >
        {strings.downloadPng}
      </button>
    </div>
  );
}

function copyLine(
  values: KinValues,
  entries: Entries,
  fmt: Fmt,
  sig: number
): string {
  return KIN_VARS.map((name) => {
    const unit = entries[name].unit;
    return `${name} = ${formatSig(fmt, valueIn(name, values, unit), sig)} ${unitSymbol(KIN_FAMILIES[name], unit)}`;
  }).join("; ");
}

function Results({
  known,
  entries,
  result,
  pick,
  onPick,
  strings,
  fmt,
  sig,
}: {
  known: KinKnown;
  entries: Entries;
  result: Extract<ReturnType<typeof solveKinematics>, { ok: true }>;
  pick: number;
  onPick: (index: number) => void;
  strings: Strings;
  fmt: Fmt;
  sig: number;
}) {
  const solution =
    result.solutions[Math.min(pick, result.solutions.length - 1)];
  const { values } = solution;
  const unknown = KIN_VARS.filter((name) => known[name] === undefined);
  const average = values.t > 0 ? values.s / values.t : null;
  const speedUnit = entries.v.unit;
  return (
    <>
      {result.solutions.length > 1 ? (
        <>
          <Notice>{strings.twoSolutions}</Notice>
          <PillGroup
            label={strings.solutionChoice}
            name="kin-solution"
            onChange={(value) => onPick(Number(value))}
            options={result.solutions.map((_, index) => ({
              value: String(index),
              label: t(strings.solutionLabel, { n: index + 1 }),
            }))}
            value={String(pick)}
          />
        </>
      ) : null}
      {result.discardedNegativeTime ? (
        <Notice>{strings.discarded}</Notice>
      ) : null}
      <StatGrid>
        {unknown.map((name) => (
          <StatCard
            emphasis
            key={name}
            label={strings[name]}
            value={`${formatSig(fmt, valueIn(name, values, entries[name].unit), sig)} ${unitSymbol(KIN_FAMILIES[name], entries[name].unit)}`}
          />
        ))}
        {average === null ? null : (
          <StatCard
            label={strings.averageVelocity}
            value={`${formatSig(fmt, fromBase(average, KIN_FAMILIES.v, speedUnit), sig)} ${unitSymbol(KIN_FAMILIES.v, speedUnit)}`}
          />
        )}
      </StatGrid>
      <CopyButton
        copiedLabel={strings.copied}
        label={strings.copy}
        small
        text={copyLine(values, entries, fmt, sig)}
      />
      <Working
        heading={strings.workingHeading}
        steps={workingSteps(known, entries, solution, strings, fmt, sig)}
      />
      {values.t > 0 ? (
        <>
          <h3 className={WIDGET_H3}>{strings.graphHeading}</h3>
          <VelocityGraph
            entries={entries}
            fmt={fmt}
            sig={sig}
            strings={strings}
            values={values}
          />
        </>
      ) : null}
      <p className={HINT}>{strings.assumptions}</p>
    </>
  );
}

interface Reading {
  known: KinKnown;
  filled: KinVar[];
  unreadable: KinVar | null;
}

function readEntries(entries: Entries): Reading {
  const known: KinKnown = {};
  const filled: KinVar[] = [];
  for (const name of KIN_VARS) {
    const raw = entries[name].value.trim();
    if (raw === "") {
      continue;
    }
    const parsed = parseQuantity(raw);
    if (parsed === null) {
      return { known, filled, unreadable: name };
    }
    filled.push(name);
    known[name] = toBase(parsed, KIN_FAMILIES[name], entries[name].unit);
  }
  return { known, filled, unreadable: null };
}

function Outcome({
  entries,
  pick,
  onPick,
  strings,
  fmt,
  sig,
  reading,
}: {
  entries: Entries;
  pick: number;
  onPick: (index: number) => void;
  strings: Strings;
  fmt: Fmt;
  sig: number;
  reading: Reading;
}) {
  if (reading.unreadable !== null) {
    return (
      <ErrorNotice>
        {t(strings.unreadable, { name: strings[reading.unreadable] })}
      </ErrorNotice>
    );
  }
  if (reading.filled.length < 3) {
    return (
      <Notice>{t(strings.needThree, { count: reading.filled.length })}</Notice>
    );
  }
  if (reading.filled.length > 3) {
    return <ErrorNotice>{strings.tooMany}</ErrorNotice>;
  }
  const result = solveKinematics(reading.known);
  if (!result.ok) {
    return <ErrorNotice>{strings.errors[result.error]}</ErrorNotice>;
  }
  return (
    <Results
      entries={entries}
      fmt={fmt}
      known={reading.known}
      onPick={onPick}
      pick={pick}
      result={result}
      sig={sig}
      strings={strings}
    />
  );
}

export default function KinematicsCalculatorWidget() {
  const strings = useToolUi(KINEMATICS_CALCULATOR_STRINGS);
  const fmt = useToolNumber();
  const locale = useToolLocale();
  const [entries, setEntries] = useState<Entries>(INITIAL);
  const [sigFigs, setSigFigs] = useState("4");
  const [pick, setPick] = useState(0);
  const sig = Number(sigFigs);
  const reading = readEntries(entries);
  const result =
    reading.unreadable === null && reading.filled.length === 3
      ? solveKinematics(reading.known)
      : null;
  const solution =
    result?.ok === true
      ? result.solutions[Math.min(pick, result.solutions.length - 1)]
      : null;

  const patch = (name: KinVar, change: Partial<Entry>) => {
    setEntries({ ...entries, [name]: { ...entries[name], ...change } });
    setPick(0);
  };

  return (
    <>
      <p className={HINT}>{strings.instructions}</p>
      <div className="grid gap-4 sm:grid-cols-2">
        {KIN_VARS.map((name) => {
          const shown =
            solution !== null && reading.known[name] === undefined
              ? formatSig(
                  fmt,
                  valueIn(name, solution.values, entries[name].unit),
                  sig
                )
              : null;
          return (
            <QuantityField
              calculated={shown}
              hint={shown === null ? undefined : strings.calculated}
              id={`kin-${name}`}
              invalid={reading.unreadable === name}
              key={name}
              label={strings[name]}
              onUnit={(unit) => patch(name, { unit })}
              onValue={(value) => patch(name, { value })}
              unit={entries[name].unit}
              unitLabel={t(strings.unitFor, { name: strings[name] })}
              units={unitOptions(KIN_FAMILIES[name])}
              value={entries[name].value}
            />
          );
        })}
      </div>
      <div className="flex flex-wrap items-end gap-4">
        <div className="w-56">
          <SelectField
            id="kin-sig"
            label={strings.rounding}
            onChange={setSigFigs}
            options={SIG_FIG_CHOICES.map((choice) => ({
              value: choice,
              label: tPlural(strings.sigFigs, locale, Number(choice), {
                count: fmt(Number(choice)),
              }),
            }))}
            value={sigFigs}
          />
        </div>
        <button
          className={BUTTON_SECONDARY}
          onClick={() => {
            setEntries({
              ...entries,
              s: { ...entries.s, value: "" },
              u: { ...entries.u, value: "" },
              v: { ...entries.v, value: "" },
              a: { ...entries.a, value: "" },
              t: { ...entries.t, value: "" },
            });
            setPick(0);
          }}
          type="button"
        >
          {strings.clear}
        </button>
      </div>
      <section aria-live="polite" className="space-y-4">
        <h2 className={WIDGET_H2}>{strings.results}</h2>
        <Outcome
          entries={entries}
          fmt={fmt}
          onPick={setPick}
          pick={pick}
          reading={reading}
          sig={sig}
          strings={strings}
        />
      </section>
    </>
  );
}
